import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import { Dialog, DialogActions, DialogContent } from '@material-ui/core';
import { Grid, LinearProgress, Button, FormControlLabel, Checkbox } from '@material-ui/core';

import { setSnackbar } from '../redux/workBackendSlice';
import { StoryCard, MapCard, GridCard, CardDb, isMapCard, isGridCard, isStoryCard } from '../redux/cardDbSlice';
import useCurrentUserId from '../hooks/useCurrentUserId';
import useCurrentDb from '../hooks/useCurrentDb';
import useUserCondition from '../hooks/useUserCondition';
import { getTimestamp } from '../general';
import { getCardSize, getStoryCardLines } from '../card_components';
import { getLineWidth, getLineHeight } from './StorySheet';

const ScreeanshotArea = styled.div`
  max-height: 70vh;
  overflow: auto;
  border: solid 2px;
  transform: scale(0.9);
`

type DownloadScreenshotDialogProps = {
  open: boolean;
  onClose: () => void;
  target: HTMLElement | null;
  ids: string[];
  cards: CardDb[];
}
const DownloadScreenshotDialog: React.FC<DownloadScreenshotDialogProps> = ({ open, onClose, target, ids, cards }) => {
  const dispatch = useDispatch();
  const userId = useCurrentUserId();
  const [sheetId] = useCurrentDb();
  const [, condition] = useUserCondition(userId);
  const [canvas, setCanvas] = useState<HTMLCanvasElement>();
  const [displaySheetName, setDisplaySheetName] = useState(true);
  const workAreaHeaderHeight = 70;

  const getCanvasWidth = () => {
    if (cards.length === 0 || !sheetId) return;
    const zoomRate = condition?.zoomRates[sheetId];
    const zoomCoefficient = typeof zoomRate === 'number' ? zoomRate / 100 : 1;
    const firstCard = cards[0];
    if (isStoryCard(firstCard)) {
      const lineObj = getStoryCardLines(ids, cards as StoryCard[]);
      const margin = 16;
      const widths = lineObj.rootIds.map((id, ix) => {
        const rootCard = lineObj.rootCards[ix];
        const children = lineObj.childrenHash[id];
        const width = getLineWidth(rootCard, children ? children.cards : undefined) + margin;
        return width;
      });
      widths.sort((a, b) => b - a);
      return widths[0] * zoomCoefficient;
    } else {
      cards.sort((a, b) => {
        const aWidth = getCardSize(a).width;
        const bWidth = getCardSize(b).width;
        if ((isMapCard(a) || isGridCard(a)) && (isMapCard(b) || isGridCard(b))) {
          return (b.x + bWidth) - (a.x + aWidth);
        } else return -1;
      })
      const horizontalEndCard = cards[0] as MapCard | GridCard;
      const cardWidth = getCardSize(horizontalEndCard).width;
      return (horizontalEndCard.x + cardWidth + 12) * zoomCoefficient;
    }
  }
  const getCanvasHeight = () => {
    if (cards.length === 0 || !sheetId) return;
    const zoomRate = condition?.zoomRates[sheetId];
    const zoomCoefficient = typeof zoomRate === 'number' ? zoomRate / 100 : 1;
    const firstCard = cards[0];
    if (isStoryCard(firstCard)) {
      const lineObj = getStoryCardLines(ids, cards as StoryCard[]);
      const margin = 8;
      const heights = lineObj.rootIds.map((id, ix) => {
        const rootCard = lineObj.rootCards[ix];
        const children = lineObj.childrenHash[id];
        const height = getLineHeight(rootCard, children ? children.cards : undefined) + margin;
        return height;
      });
      const canvasHeight = heights.reduce((pre, current) => pre + current);
      if (displaySheetName) return canvasHeight * zoomCoefficient + 82;
      else return canvasHeight * zoomCoefficient + 82 - workAreaHeaderHeight;
    } else {
      cards.sort((a, b) => {
        const aHeight = getCardSize(a).height;
        const bHeight = getCardSize(b).height;
        if ((isMapCard(a) || isGridCard(a)) && (isMapCard(b) || isGridCard(b))) {
          return (b.y + bHeight) - (a.y + aHeight);
        } else return -1;
      })
      const verticalEndCard = cards[0] as MapCard | GridCard;
      const cardHeight = getCardSize(verticalEndCard).height;
      if (displaySheetName) return (verticalEndCard.y + cardHeight + 12) * zoomCoefficient + 82;
      else return (verticalEndCard.y + cardHeight + 12) * zoomCoefficient + 82 - workAreaHeaderHeight;
    }
  }
  const copyToClipboard = () => {
    if (!canvas) {
      dispatch(setSnackbar({ open: true, message: 'コピーに失敗しました。'}));
      return;
    }
    navigator.permissions.query({ name: 'clipboard-write' as any }).then(res => {
      if (res.state === 'denied') {
        dispatch(setSnackbar({ open: true, message: 'コピーに失敗しました。'}));
        return;
      }
      canvas.toBlob(blob => {
        if (!blob) {
          dispatch(setSnackbar({ open: true, message: 'コピーに失敗しました。'}));
          return;
        }
        const data = [new ClipboardItem({ [blob.type]: blob })];
        navigator.clipboard.write(data).then(() => {
          dispatch(setSnackbar({ open: true, message: 'クリップボードにコピーしました。'}));
          onClose();
        });
      });
    });
  }
  const download = () => {
    if (!canvas) return;
    const link = document.createElement('a');
    link.href = canvas.toDataURL();
    const timestamp = getTimestamp();
    link.download = 'digimagi-screenshot_' + timestamp + '.png';
    link.click();
    onClose();
  }
  const adjustDisplay = (clonedDocument: Document, clonedElement: HTMLElement) => {
    clonedElement.style.overflow = 'visible';

    if (!displaySheetName) {
      const sheetNameTextField = clonedDocument.getElementById('word-area-header');
      if (sheetNameTextField) sheetNameTextField.remove();
    }
  }

  useEffect(() => {
    if (!target) return;
    const canvasWidth = getCanvasWidth();
    const canvasHeight = getCanvasHeight();
    const { left: offsetX, top } = target.getBoundingClientRect();
    const offsetY = displaySheetName ? top : top - 24;
    html2canvas(target, { foreignObjectRendering: true, x: -offsetX, y: -offsetY, width: canvasWidth, height: canvasHeight, onclone: adjustDisplay }).then(value => {
      setCanvas(value)
      const area = document.getElementById('screenshot-area');
      if (!area) return;
      area.replaceChildren();
      area.appendChild(value);
    })
  }, [open, displaySheetName])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl">
      <DialogContent>
        <ScreeanshotArea id="screenshot-area">
          { !canvas && <LinearProgress /> }
        </ScreeanshotArea>
        { canvas &&
          <Grid container justifyContent="center" style={{ marginBottom: 16 }}>
            <FormControlLabel label="シート名を表示する"
              control={
                <Checkbox checked={displaySheetName} color="primary"
                  onChange={(ev) => setDisplaySheetName(ev.currentTarget.checked)} />
              } />
          </Grid>
        }
        <Grid container justifyContent="center" spacing={8}>
          <Grid item>
            <Button color='primary' onClick={() => copyToClipboard()} disabled={!canvas}>
              クリップボードにコピー
            </Button>
          </Grid>
          <Grid item>
            <Button color='primary' onClick={() => download()} disabled={!canvas}>
              ファイルに保存
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button color='primary' onClick={onClose}>
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DownloadScreenshotDialog;
